import { Chip, Grid, Paper, Stack, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback } from 'react'
import { DatePicker } from '@e3dc-react/shell/Elements/DatePicker'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { max } from '@e3dc-react/shell/Libs/date'
import moment, { Moment } from 'moment'
import { getDateFormat, getDateViews } from './chart/chart-date-formatting'
import { ChartDateRange, ChartDateRangeView, chartDateRangeViews, CUSTOM_TABLET_BREAKPOINT } from '../models/dashboard.model'
import { getDates } from './chart/get-dates'
import { calculateNextDateRange } from './chart/calculate-next-date-range'

interface ChartDateRangeFilterProps {
  value: ChartDateRange
  onChange: (range: ChartDateRange) => void
  minDate: Date | undefined
  maxDate: Moment
  leftSlot?: JSX.Element
}

export const ChartDateRangeFilter: React.FC<ChartDateRangeFilterProps> = ({ value, onChange, minDate, maxDate, leftSlot }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isCustomTablet = useMediaQuery(theme.breakpoints.down(CUSTOM_TABLET_BREAKPOINT))

  const handleViewChange = useCallback(
    (view: ChartDateRangeView) => {
      /**
       * This handler implements the following behavior when switching between "day", "year", "month" and "week" view of the
       * date range picker:
       *
       * 1. Switching from Year to Month
       *        When a year (e.g., 2024) is displayed and the user switches to the month view, the first month of the
       *        displayed year (i.e., January 2024) should be shown.
       *
       * 2. Switching from Month to Week
       *        When a month (e.g., August 2024) is displayed and the user switches to the week view, the first week of
       *        the selected month should be shown. The week that includes the first day of the month is considered the
       *        first week of the month.
       *
       * 3. Switching from Week to Month
       *        When a week within a month is displayed, the corresponding month containing this week should be shown
       *        (e.g., the week from August 28 to September 3, 2024, when switched to the month view, should display August).
       *        The min date of the week view displayed should be used as a reference for the month to be selected next.
       *
       * 4. Switching from Month to Year
       *        When a month (e.g., August 2024) is displayed and the user switches to the year view,
       *        the year (2024) containing this month should be displayed.
       *
       * For all these cases, any time a new start date is calculated, we must put a lower limit to it. The limit is the
       * first session ever created and is calculated by finding the greater date between the theoretical min date and
       * the start date of the first session ever created on this installation.
       */
      const prevView = value.view

      if (view === prevView) {
        /**
         * This logic is triggered when user clicks one of the "views" twice in a row("week" -> "week", "month" -> "month" or
         * "year" -> "year").
         *
         * When this happens, we reset the range to the latest(today's) week, month or year, regardless of what range was
         * previously shown to the user.
         *
         * After this, no additional date calculations are applied.
         */
        const { startOfDate, endOfDate } = getDates(new Date(), view)
        const newEndDate = endOfDate
        const newStartDate = max([new Date(minDate ?? ''), startOfDate])

        onChange({ startDate: newStartDate.toISOString(), endDate: newEndDate.toISOString(), view })

        return
      }

      const { newStartDate, newEndDate } = calculateNextDateRange({
        previousStartDate: new Date(value.startDate),
        previousView: value.view,
        nextView: view,
        minDate: new Date(minDate ?? ''),
      })

      const clampedStartDate = max([new Date(minDate ?? ''), newStartDate])

      onChange({
        startDate: clampedStartDate.toISOString(),
        endDate: newEndDate.toISOString(),
        view,
      })
    },
    [value.view, value.startDate, minDate, onChange]
  )

  const handleNextClick = useCallback(() => {
    const { onNextStartDate, onNextEndDate } = getDates(new Date(value.startDate), value.view)
    onChange({ startDate: onNextStartDate.toISOString(), endDate: onNextEndDate.toISOString(), view: value.view })
  }, [onChange, value.startDate, value.view])

  const handlePrevClick = useCallback(() => {
    const { onPreviousStartDate, onPreviousEndDate } = getDates(new Date(value.startDate), value.view)
    onChange({ startDate: onPreviousStartDate.toISOString(), endDate: onPreviousEndDate.toISOString(), view: value.view })
  }, [onChange, value.startDate, value.view])

  const handleDateChange = useCallback(
    (newDate: Moment | null) => {
      if (!newDate) {
        return
      }

      const { startOfDate, endOfDate } = getDates(newDate, value.view)
      onChange({ startDate: startOfDate.toISOString(), endDate: endOfDate.toISOString(), view: value.view })
    },
    [onChange, value.view]
  )

  if (isCustomTablet) {
    return (
      <Paper id="e-mobility-dashboard-chart-filter">
        <Grid container alignItems="center" mt={0.5}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={1}>
              {chartDateRangeViews.map(view => (
                <Chip
                  onClick={() => {
                    handleViewChange(view)
                  }}
                  label={t(`emobility.chargingSession.${view}`)}
                  color={view === value.view ? 'primary' : 'default'}
                  key={`e-mobility-chart-date-range-view-button-${view}`}
                  sx={{ width: 100 }}
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={7}>
            <DatePicker
              /**
               * "key" prop note:
               *
               * DatePicker implementation contains a nested memoized MUI TextField which does not re-render unless
               * "value" prop is changed due to its internal state management strategy.
               *
               * This can lead to a specific issue:
               *
               * Let's say the latest session occurred during the week 49 of 2024([1 Dec, 6 Dec]). Selecting date range "week/49" will
               * update the date range label to "49/2024" - as expected. However, if we immediately after select the "month" option, it would
               * default to "month/12" as the latest month which contains sessions, BUT will not update the label to "12/2024" because the "value"
               * stays the same("1 Dec").
               *
               * To force re-render of the whole date picker, we set the "view" option(week, month, year)" as the key. Whenever it changes,
               * the whole date picker should re-render and re-calculate the label value as well.
               */
              onNextClick={handleNextClick}
              onPrevClick={handlePrevClick}
              key={value.view}
              disableFuture
              value={moment(value.startDate)}
              maxFrom={moment().startOf(value.view)}
              maxDate={maxDate}
              inputFormat={getDateFormat(value.view)}
              minDate={moment(minDate).startOf(value.view)}
              handleDateChange={handleDateChange}
              views={getDateViews(value.view)}
              fullWidth
            />
          </Grid>

          <Grid item xs={5}>
            <Stack direction="row" justifyContent="flex-end" width="100%">
              {leftSlot}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  return (
    <Paper id="e-mobility-dashboard-chart-filter">
      <Grid container alignItems="center" mt={0.5}>
        <Grid item xs={3} lg={4}>
          <Stack width="100%" direction="row" justifyContent={isCustomTablet ? 'flex-end' : 'flex-start'}>
            {leftSlot}
          </Stack>
        </Grid>

        <Grid item xs={5} lg={4} width="100%" direction="row" justifyContent="center">
          <DatePicker
            /**
             * "key" prop note:
             *
             * DatePicker implementation contains a nested memoized MUI TextField which does not re-render unless
             * "value" prop is changed due to its internal state management strategy.
             *
             * This can lead to a specific issue:
             *
             * Let's say the latest session occurred during the week 49 of 2024([1 Dec, 6 Dec]). Selecting date range "week/49" will
             * update the date range label to "49/2024" - as expected. However, if we immediately after select the "month" option, it would
             * default to "month/12" as the latest month which contains sessions, BUT will not update the label to "12/2024" because the "value"
             * stays the same("1 Dec").
             *
             * To force re-render of the whole date picker, we set the "view" option(week, month, year)" as the key. Whenever it changes,
             * the whole date picker should re-render and re-calculate the label value as well.
             */
            key={value.view}
            value={moment(value.startDate)}
            minDate={moment(minDate).startOf(value.view)}
            maxFrom={moment().startOf(value.view)}
            maxDate={maxDate}
            views={getDateViews(value.view)}
            inputFormat={getDateFormat(value.view)}
            disableFuture
            handleDateChange={handleDateChange}
            onNextClick={handleNextClick}
            onPrevClick={handlePrevClick}
            fullWidth
          />
        </Grid>

        <Grid item xs={4} lg={4}>
          <Stack direction="row" columnGap={1} justifyContent="flex-end" alignItems="center">
            {chartDateRangeViews.map(view => (
              <Chip
                key={`e-mobility-chart-date-range-view-button-${view}`}
                label={t(`emobility.chargingSession.${view}`)}
                color={view === value.view ? 'primary' : 'default'}
                onClick={() => {
                  handleViewChange(view)
                }}
                sx={{ width: 'max-content' }}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  )
}
